import React, { FC, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Badge,
  Button,
  Card,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Form,
  Input,
  message,
  Modal,
  Progress,
  Table,
  Tag,
  Tooltip,
  Dropdown as AntDropdown,
} from "antd";
import type { MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Icon, TooltipComponent } from "../../../components/Component";
import { logHoursApis } from "../../../api/TimeLog";
import { tasksApis } from "../../../api/NewTasks";
import { ColumnType } from "antd/es/table";
import TimeSheetListCreation from "./TimeSheetListCreation";
import { workApprovalApis } from "../../../api/WorkApproval";
import Swal from "sweetalert2";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import {
  formatCurrency,
  resourceLineTotal,
  toNumber,
  toolLineTotal,
} from "./timelogAmountUtils";

interface ApprovalItem {
  stateApprovalId?: number;
  approvalStatus: "Pending" | "Approved" | "Rejected";
  approvalLinkId: string;
  timeLogId?: number;
  timeLogTitle?: string;
  rawData?: any;
}
export interface TimeLogListViewProps {
  timeLogId: number;
  projectId: number;
  taskId?: number;
  issueId?: number;
  timeLogTitle?: string;
  projectTitle?: string;
  user: string;
  dailyLogHours: string;
  timePeriod: string;
  billingType: string;
  notes: string;
  createdBy: string;
  createdAt?: string;
  approvalStatus?: "Pending" | "Approved" | "Rejected"; // Added optional property
  stateApprovalId?: number; // Added optional property
  approvalLinkId?: string;
  rawData?: any;
  taskCompletionPercentage?: number | null;
}

interface TimeLogTableProps {
  projectId?: number;
  projectName?: string;
  taskId?: number;
  issueId?: number;
  showAddButton?: boolean;
  isApprover?: boolean;
  activeTab?: string;
  isFromWorkUI?: boolean;
  moduleName?: string; // Add moduleName prop for flexible access control
  isFromSpecificLevel?: boolean;
  taskOwnerId?: number; // Add task owner ID for access control
  taskOwnerName?: string; // Add task owner name for display
  issueOwnerId?: number; // Add issue owner ID for access control
  issueOwnerName?: string; // Add issue owner name for display
  onTaskCompletionUpdate?: () => void; // Callback for task completion updates
  refreshParentTask?: () => void;
  // When false, the Add Time Log button is hidden. Driven by the task API's
  // `allowTaskUpdates` field — backend authoritative gate.
  allowTaskUpdates?: boolean;
}

export const TimeLogTable: FC<TimeLogTableProps> = ({
  projectId,
  projectName,
  taskId,
  issueId,
  showAddButton = true,
  // isApprover = false,
  activeTab,
  isFromWorkUI = false,
  moduleName = "Tasks", // Default to Tasks for backward compatibility
  isFromSpecificLevel,
  taskOwnerId,
  taskOwnerName,
  issueOwnerId,
  issueOwnerName,
  onTaskCompletionUpdate,
  refreshParentTask,
  allowTaskUpdates = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  // id -> name maps to label tool consumed-materials in the detail view.
  const [materialNameMap, setMaterialNameMap] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    (async () => {
      try {
        const res = await tasksApis.fetchMachineryMaterialDropDown();
        if (res?.status) {
          const map: Record<number, string> = {};
          (res.data || []).forEach((m: any) => {
            map[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
          });
          setMaterialNameMap(map);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    total: 0,
  });

  // approval
  const [currentRejectItem, setCurrentRejectItem] =
    useState<ApprovalItem | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  // Utility functions
  const secondsToHHMM = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const to12HourTime = (time?: string): string => {
    if (!time || !time.includes(":")) return "--:--";

    const [h, m] = time.split(":");
    let hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${m} ${ampm}`;
  };

  const handleSelectTimeLog = (logItem: any) => {
    const rawData = logItem.rawData || logItem;

    navigate(`/pms/project/timelog-detail/${rawData.timeLogId}`, {
      state: {
        projectId: rawData.projectId || projectId,
        projectName: rawData.projectName || projectName,
        taskId: rawData.taskId,
        taskName: rawData.taskName,
        issueId: rawData.issueId,
        issueName: rawData.issueName,
        timeLogId: rawData.timeLogId,
        timeLogTitle: `Time Log #${rawData.timeLogId}`,
        returnPath: location.pathname,
        returnState: {
          ...((location.state as any) || {}),
          activeTab,
        },
      },
    });
  };

  // approvals
  const findPendingApprovalForCurrentUser = (stateApprovals: any[] = []) => {
    if (!Array.isArray(stateApprovals)) return undefined;

    const pendingForCurrentUser = stateApprovals.find(
      (approval: any) =>
        approval?.userId === currentUser?.id &&
        (approval?.status === "Pending" ||
          approval?.approvalStatus === "Pending")
    );

    if (pendingForCurrentUser) return pendingForCurrentUser;

    return stateApprovals.find(
      (approval: any) =>
        approval?.status === "Pending" || approval?.approvalStatus === "Pending"
    );
  };

  const handleApprove = async (record: TimeLogListViewProps) => {
    try {
      // Find the stateApprovalId from the stateApprovals array
      const stateApproval = findPendingApprovalForCurrentUser(
        record.rawData?.stateApprovals
      );
      if (!stateApproval) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Pending approval data not found for this user",
          confirmButtonText: "OK",
        });
        return;
      }

      const response = await workApprovalApis.approveState({
        stateApprovalId: stateApproval.stateApprovalId,
      });

      if (!response?.status) {
        const apiError =
          response?.response?.data?.message ||
          response?.message ||
          "Failed to approve time log";
        const apiTitle =
          response?.response?.data?.title ||
          response?.response?.data?.error ||
          "Error";
        await Swal.fire({
          icon: "error",
          title: apiTitle,
          text: apiError,
          confirmButtonText: "OK",
        });
        return;
      }

      const successMessage =
        response?.data?.message ||
        response?.message ||
        "Time log approved successfully";

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text: successMessage,
        confirmButtonText: "OK",
      });
      refreshData(); // Refresh the list
      refreshParentTask?.();
    } catch (error: any) {
      console.error("Error approving:", error);
      await Swal.fire({
        icon: "error",
        title: error.response?.data?.title || "Error",
        text:
          error.response?.data?.message ||
          error.message ||
          "Failed to approve time log",
        confirmButtonText: "OK",
      });
    }
  };

  const handleRejectClick = (record: TimeLogListViewProps) => {
    const approvalItem: ApprovalItem = {
      stateApprovalId: record.stateApprovalId,
      approvalStatus: record.approvalStatus || "Pending",
      approvalLinkId: record.approvalLinkId || "",
      timeLogId: record.timeLogId,
      timeLogTitle: record.timeLogTitle,
      rawData: record.rawData, // Pass the raw data to access stateApprovals
    };
    setCurrentRejectItem(approvalItem);
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      if (currentRejectItem) {
        // Find the stateApprovalId from the stateApprovals array
        const stateApproval = findPendingApprovalForCurrentUser(
          currentRejectItem.rawData?.stateApprovals
        );
        if (!stateApproval) {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Pending approval data not found for this user",
            confirmButtonText: "OK",
          });
          return;
        }

        const response = await workApprovalApis.rejectState({
          stateApprovalId: stateApproval.stateApprovalId,
          reason,
        });
        if (!response?.status) {
          const apiError =
            response?.response?.data?.message ||
            response?.message ||
            "Failed to reject time log";
          const apiTitle =
            response?.response?.data?.title ||
            response?.response?.data?.error ||
            "Error";
          await Swal.fire({
            icon: "error",
            title: apiTitle,
            text: apiError,
            confirmButtonText: "OK",
          });
          return;
        }
        const successMessage =
          response?.data?.message ||
          response?.message ||
          "Time log rejected successfully";

        await Swal.fire({
          icon: "success",
          title: "Rejected",
          text: successMessage,
          confirmButtonText: "OK",
        });
        refreshData(); // Refresh the list
        refreshParentTask?.();
      }
    } catch (error: any) {
      console.error("Error rejecting:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to reject time log",
        confirmButtonText: "OK",
      });
    } finally {
      setIsRejecting(false);
      setRejectModalVisible(false);
      setCurrentRejectItem(null);
    }
  };

  // Add the RejectModal component (same as in TaskInformationById)
  const RejectModal = ({
    isModalVisible,
    setIsModalVisible,
    onReject,
    title = "Reject Time Log",
    label = "Reason for rejection",
    isLoading = false,
  }: {
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    onReject: (reason: string) => void;
    title?: string;
    label?: string;
    isLoading?: boolean;
  }) => {
    const [form] = Form.useForm();

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const handleOk = () => {
      form
        .validateFields()
        .then((values) => {
          const reason = values.reason;
          onReject(reason);
          form.resetFields();
          setIsModalVisible(false);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    };

    return (
      <Modal
        title={title}
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label={label}
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea placeholder="Type your reason here..." />
          </Form.Item>
          <div className="actions clearfix">
            <ul>
              <li>
                <Button
                  color="primary"
                  type="submit"
                  onClick={handleOk}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </Button>
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={handleCancel}
                  className="btn-dim btn btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </li>
            </ul>
          </div>
        </Form>
      </Modal>
    );
  };

  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Check if current user can add time logs
  const canAddTimeLog = () => {
    if (!currentUser) return false;

    // If no specific owner is provided (general time sheets page), allow all users to add time logs
    if (!taskOwnerId && !issueOwnerId) return true;

    // Check if user is admin - admin can always add time logs
    if (currentUser.genericRole?.toLowerCase() === "admin") return true;

    // For task-specific time log creation, only task owner can add
    if (taskOwnerId && currentUser.id === taskOwnerId) return true;

    // For issue-specific time log creation, only issue owner can add
    if (issueOwnerId && currentUser.id === issueOwnerId) return true;

    return false;
  };

  const isAdminUser =
    (currentUser?.genericRole || "").toLowerCase() === "admin";

  const isUserApproverForLog = (timeLog: any) => {
    const approvals = timeLog?.rawData?.stateApprovals;
    if (!Array.isArray(approvals) || approvals.length === 0) return false;
    if (
      approvals.some(
        (approval: any) =>
          approval.userId === currentUser?.id &&
          approval.status === "Pending"
      )
    )
      return true;
    if (
      isAdminUser &&
      approvals.some((approval: any) => approval.status === "Pending")
    )
      return true;
    return false;
  };

  // Check if there are any logs where current user is an approver
  const hasApproverLogs = dataSource.some(isUserApproverForLog);

  const getCompletionDisplay = (percentage?: number | null) => {
    if (percentage === null || percentage === undefined) {
      return "--";
    }
    return `${Math.round(percentage)}%`;
  };

  const columns: ColumnType<TimeLogListViewProps>[] = [
    {
      title: "Log Title",
      dataIndex: "timeLogTitle",
      key: "timeLogTitle",
      fixed: "left",
      width: 280,
      render(text: any, record: any) {
        const rawData = record?.rawData || {};
        const moduleType = rawData?.moduleType || "Task";
        const titleText =
          moduleType === "Issue"
            ? rawData?.issueName || text
            : rawData?.taskName || text;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Badge
              size="small"
              color={moduleType === "Issue" ? "orange" : "blue"}
            >
              {moduleType}
            </Badge>

            <Tooltip title={titleText} placement="topLeft">
              <span
                className="inlineTableCellLink"
                onClick={() => handleSelectTimeLog(record)}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  maxWidth: "220px",
                  cursor: "pointer",
                }}
              >
                {titleText}
              </span>
            </Tooltip>
          </div>
        );
      },
    },

    // {
    //   title: "Project",
    //   dataIndex: "projectTitle",
    //   key: "projectTitle",
    //   width: 250,
    //   render: (text: string) => <>{text}</>,
    // },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 150,
      render: (_, record) => {
        // For admin users in task-specific context, show task owner name instead of time log creator
        if (
          currentUser?.genericRole?.toLowerCase() === "admin" &&
          taskOwnerName &&
          isFromSpecificLevel
        ) {
          return <>{taskOwnerName}</>;
        }
        return <>{record?.user}</>;
      },
    },
    {
      title: "Log Date",
      dataIndex: "logDate",
      key: "logDate",
      width: 100,
      render: (text) =>
        text && text !== "--" ? dayjs(text).format("DD MMM YYYY") : "--", // Handle empty or fallback value
    },
    {
      title: "Daily Log Hours",
      dataIndex: "dailyLogHours",
      key: "dailyLogHours",
      width: 180,
      render: (text: string) => <>{text}</>,
    },
    {
      title: "Time Period",
      dataIndex: "timePeriod",
      key: "timePeriod",
      width: 150,
      render: (text: string) => <>{text}</>,
    },
    {
      title: "Billing Type",
      dataIndex: "billingType",
      key: "billingType",
      width: 100,
      render: (text: string) => <>{text}</>,
    },
    {
      title: "Completion %",
      dataIndex: "taskCompletionPercentage",
      key: "taskCompletionPercentage",
      width: 200,
      render: (_: any, record: TimeLogListViewProps) => {
        const percentage =
          record.taskCompletionPercentage ??
          record.rawData?.taskCompletionPercentage ??
          record.rawData?.completionPercentage;
        if (percentage === null || percentage === undefined) return "--";
        const percent =
          typeof percentage === "number" ? percentage : Number(percentage);
        if (isNaN(percent)) return "--";
        return (
          <Tooltip title={`${Math.round(percent)}% Complete`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percent}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 150,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "150px",
              cursor: "pointer",
            }}
          >
            {text || "—"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Total Amount",
      key: "totalAmount",
      width: 140,
      align: "right" as const,
      render: (_: any, record: TimeLogListViewProps) => {
        const raw = record.rawData || {};
        const total =
          raw.totalAmount ??
          toNumber(raw.resourcesTotalAmount) + toNumber(raw.toolsTotalAmount);
        const value = toNumber(total);
        return value ? (
          <strong style={{ color: "#004293" }}>{formatCurrency(value)}</strong>
        ) : (
          "--"
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      // fixed: "right",
      render: (text) =>
        text ? dayjs(text).format("DD MMM YYYY, hh:mm A") : "--",
    },
    {
      title: "Approval Status",
      dataIndex: "stateApprovals",
      key: "approvalStatus",
      width: 150,
      fixed: "right" as const,
      render: (
        approvals: Array<{ status: string }> = [],
        record: TimeLogListViewProps
      ) => {
        // if (!isUserApproverForLog(record)) return null;
        const status = record.approvalStatus || "N/A";
        let color = "orange"; // default
        if (status === "Approved") color = "green";
        else if (status === "Rejected") color = "#e74c3c";
        else if (status === "N/A") return "N/A";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    ...(hasApproverLogs
      ? [
          {
            title: "Action",
            key: "action",
            width: 120,
            fixed: "right" as const,
            render: (_: any, record: TimeLogListViewProps) => {
              if (!isUserApproverForLog(record)) return null;

              const hasPendingApproval = record.rawData?.stateApprovals?.some(
                (approval: any) => approval.status === "Pending"
              );

              const items: MenuProps["items"] = [
                {
                  key: "approve",
                  label: "Approve",
                  icon: <Icon name="check" />,
                  disabled: !hasPendingApproval,
                  onClick: () => handleApprove(record),
                },
                {
                  key: "reject",
                  label: "Reject",
                  icon: <Icon name="cross" />,
                  disabled: !hasPendingApproval,
                  onClick: () => handleRejectClick(record),
                },
              ];
              return (
                <div className="d-flex align-items-center">
                  <AntDropdown
                    menu={{ items }}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={() => document.body}
                  >
                    <a
                      onClick={(e) => e.preventDefault()}
                      style={{
                        padding: "4px",
                        fontSize: "16px",
                        color: "#555",
                        cursor: "pointer",
                        display: "inline-flex",
                      }}
                    >
                      <MoreOutlined />
                    </a>
                  </AntDropdown>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  // Section-total footer used inside the expanded detail.
  const DetailTotalRow: FC<{
    label: string;
    amount: number;
    strong?: boolean;
  }> = ({ label, amount, strong }) => (
    <div
      className="d-flex justify-content-end align-items-center mt-2"
      style={{
        gap: 8,
        fontWeight: strong ? 700 : 600,
        color: "#004293",
        ...(strong ? { borderTop: "1px solid #e8e8e8", paddingTop: 8 } : {}),
      }}
    >
      <span>{label}:</span>
      <span style={{ fontSize: strong ? 15 : 14 }}>
        {formatCurrency(amount)}
      </span>
    </div>
  );

  // Expanded row: actual resource/tool values captured at creation, with
  // per-line, section, and grand totals.
  const renderTimeLogDetail = (record: TimeLogListViewProps) => {
    const raw = record.rawData || {};
    const resources: any[] = raw.timeLogResources || [];
    const tools: any[] = raw.timeLogTools || [];

    const resourceLineAmount = (r: any) =>
      r.totalAmount !== undefined && r.totalAmount !== null
        ? toNumber(r.totalAmount)
        : resourceLineTotal(r.actualResourceCount, r.actualQuantity, r.actualRate);
    const toolLineAmount = (t: any) =>
      t.totalAmount !== undefined && t.totalAmount !== null
        ? toNumber(t.totalAmount)
        : toolLineTotal(t.actualQuantity, t.actualRate);

    const resourcesTotal =
      raw.resourcesTotalAmount !== undefined && raw.resourcesTotalAmount !== null
        ? toNumber(raw.resourcesTotalAmount)
        : resources.reduce((s, r) => s + resourceLineAmount(r), 0);
    const toolsTotal =
      raw.toolsTotalAmount !== undefined && raw.toolsTotalAmount !== null
        ? toNumber(raw.toolsTotalAmount)
        : tools.reduce((s, t) => s + toolLineAmount(t), 0);
    const grandTotal =
      raw.totalAmount !== undefined && raw.totalAmount !== null
        ? toNumber(raw.totalAmount)
        : resourcesTotal + toolsTotal;

    const withUom = (qty: any, uom: any) =>
      qty !== undefined && qty !== null && qty !== ""
        ? `${qty}${uom ? ` ${uom}` : ""}`
        : "-";

    return (
      <div style={{ padding: "4px 8px" }}>
        {resources.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h6 className="mb-2" style={{ color: "#004293", fontWeight: 600 }}>
              Resources
            </h6>
            <Table
              rowKey={(r: any) => `${r.timeLogResourceId}`}
              dataSource={resources}
              pagination={false}
              size="small"
              bordered
              scroll={{ x: "max-content" }}
              columns={[
                {
                  title: "Labour Type",
                  dataIndex: "laborTypeName",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Labour Category",
                  dataIndex: "laborCategoryName",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Resource at Site",
                  dataIndex: "actualResourceCount",
                  align: "center" as const,
                  render: (t: any) => t ?? "-",
                },
                {
                  title: "Actual Quantity",
                  align: "center" as const,
                  render: (_: any, r: any) =>
                    withUom(r.actualQuantity, r.uomName),
                },
                {
                  title: "Actual Rate",
                  dataIndex: "actualRate",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Total Amount",
                  align: "right" as const,
                  render: (_: any, r: any) =>
                    formatCurrency(resourceLineAmount(r)),
                },
              ]}
            />
            <DetailTotalRow label="Resources Total" amount={resourcesTotal} />
          </div>
        )}

        {tools.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <h6 className="mb-2" style={{ color: "#004293", fontWeight: 600 }}>
              Tools &amp; Machinery
            </h6>
            <Table
              rowKey={(t: any) => `${t.timeLogToolId}`}
              dataSource={tools}
              pagination={false}
              size="small"
              bordered
              scroll={{ x: "max-content" }}
              columns={[
                {
                  title: "Tool Type",
                  dataIndex: "toolTypeName",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Tool/Machinery",
                  dataIndex: "toolName",
                  align: "center" as const,
                  render: (t: any, r: any) =>
                    (t ?? "") !== "" ? t : `Tool #${r.toolId}`,
                },
                {
                  title: "Serial Number",
                  dataIndex: "serialNumber",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Actual Quantity",
                  align: "center" as const,
                  render: (_: any, t: any) =>
                    withUom(t.actualQuantity, t.uomName),
                },
                {
                  title: "Actual Rate",
                  dataIndex: "actualRate",
                  align: "center" as const,
                  render: (t: any) => (t ?? "") !== "" ? t : "-",
                },
                {
                  title: "Total Amount",
                  align: "right" as const,
                  render: (_: any, t: any) =>
                    formatCurrency(toolLineAmount(t)),
                },
              ]}
              expandable={{
                rowExpandable: (t: any) => (t.toolMaterials || []).length > 0,
                expandedRowRender: (t: any) => (
                  <Table
                    rowKey={(m: any) => `${m.timeLogToolMaterialId}`}
                    dataSource={t.toolMaterials || []}
                    pagination={false}
                    size="small"
                    bordered
                    title={() => "Consumed Materials"}
                    columns={[
                      {
                        title: "Material",
                        align: "center" as const,
                        render: (_: any, m: any) =>
                          materialNameMap[m.materialId] ||
                          (m.materialId ? `Material #${m.materialId}` : "-"),
                      },
                      {
                        title: "Planned Qty",
                        align: "center" as const,
                        render: (_: any, m: any) =>
                          withUom(m.plannedQuantity, m.workUomName),
                      },
                      {
                        title: "Planned Rate",
                        align: "center" as const,
                        render: (_: any, m: any) =>
                          (m.plannedRate ?? "") !== "" ? m.plannedRate : "-",
                      },
                      {
                        title: "Actual Qty",
                        align: "center" as const,
                        render: (_: any, m: any) =>
                          withUom(m.actualQuantity, m.workUomName),
                      },
                      {
                        title: "Actual Rate",
                        align: "center" as const,
                        render: (_: any, m: any) =>
                          (m.actualRate ?? "") !== "" ? m.actualRate : "-",
                      },
                      {
                        title: "Total Amount",
                        align: "right" as const,
                        render: (_: any, m: any) =>
                          formatCurrency(
                            m.totalAmount != null
                              ? toNumber(m.totalAmount)
                              : toNumber(m.actualQuantity) *
                                  toNumber(m.actualRate),
                          ),
                      },
                    ]}
                  />
                ),
              }}
            />
            <DetailTotalRow label="Tools Total" amount={toolsTotal} />
          </div>
        )}

        {resources.length === 0 && tools.length === 0 ? (
          <div style={{ color: "#666", padding: "8px 0" }}>
            No resources or tools logged for this time log.
          </div>
        ) : (
          <DetailTotalRow strong label="Grand Total" amount={grandTotal} />
        )}
      </div>
    );
  };

  const mapTimeLogList = (list: any[]): TimeLogListViewProps[] =>
    list.map((item) => {
      const formatTime = (timeStr: string) => {
        if (!timeStr) return "--:--";
        const [hour, minute] = timeStr.split(":");
        const h = parseInt(hour, 10);
        const ampm = h >= 12 ? "PM" : "AM";
        const formattedHour = h % 12 || 12;
        return `${formattedHour.toString().padStart(2, "0")}:${minute} ${ampm}`;
      };

      const formatHours = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${h.toString().padStart(2, "0")}:${m
          .toString()
          .padStart(2, "0")}`;
      };

      const stateApproval = item.stateApprovals?.[0];

      return {
        timeLogId: item.timeLogId,
        timeLogTitle: `Time Log #${item.timeLogId}`,
        projectId: item.projectId,
        projectTitle: item.projectName || `Project #${item.projectId}`,
        user: item.user || item.userName || item.createdByName || "—",
        dailyLogHours: formatHours(item.totalWorkHoursInSec || 0),
        timePeriod:
          item.loginTime && item.logoutTime
            ? `${formatTime(item.loginTime)} - ${formatTime(item.logoutTime)}`
            : "--:--",
        billingType: item.billingType || "—",
        resources: item.timelogResources || [],
        notes: item.notes || "—",
        createdBy: item.createdBy || "—",
        createdAt: item.createdAt || "—",
        taskId: item.taskId || null,
        issueId: item.issueId || null,
        rawData: item,
        logDate: item.logDate || "--",
        approvalStatus: item.approvalStatus || "Pending",
        stateApprovalId: stateApproval?.stateApprovalId,
        approvalLinkId: item.approvalLinkId || stateApproval?.approvalLinkId,
        taskCompletionPercentage:
          item.taskCompletionPercentage ?? item.completionPercentage ?? null,
      };
    });

  const fetchData = async (params: any, append = false) => {
    if (activeTab !== "loghours") return;
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
      } = params;

      const apiResponse: any = await logHoursApis.listTimeLog({
        pageSize,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds: projectId?.toString(),
        taskIds: taskId ? taskId.toString() : undefined,
        issueIds: issueId ? issueId.toString() : undefined,
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count } =
          data || apiResponse?.response.data || apiResponse.response;

        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        if (list) {
          setDataSource((prev: any) =>
            append ? [...prev, ...mapTimeLogList(list)] : mapTimeLogList(list)
          );
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction === "F"
            ? dataSource[dataSource.length - 1]?.timeLogId
            : dataSource[0]?.timeLogId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData(apiParams);
  };

  const refreshData = async () => {
    await fetchData({
      current: pagination.current,
      pageSize: pagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      projectIds: projectId?.toString(),
      taskId: taskId?.toString(),
      issueId: issueId?.toString(),
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("resize", viewChange);
    return () => window.removeEventListener("resize", viewChange);
  }, []);

  useEffect(() => {
    if (activeTab === "loghours") {
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: null,
      });
    }
  }, [projectId, taskId, issueId, activeTab]);

  return (
    <div className="card-inner p-0">
      {!isFromWorkUI && showAddButton && canAddTimeLog() && allowTaskUpdates && (
        <div className="d-flex justify-content-end mb-3">
          <AddAccess moduleName={moduleName}>
            <Button
              color="primary"
              size="sm"
              onClick={() => setModalOpen(true)}
            >
              <Icon name="plus" />
              <span>Add Time Log</span>
            </Button>
          </AddAccess>
        </div>
      )}

      {/* <div
        style={{
          width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
          margin: "0 auto",
        }}
      > */}
      <div
        className="table-responsive"
        style={{
          width: "100%",
          overflowX: "auto",
          margin: "10px 0",
        }}
      >
        <SkeletonTable
          loading={loading}
          columns={columns as SkeletonTableColumnsType[]}
        >
          <Table
            rowKey="timeLogId"
            columns={columns}
            dataSource={dataSource}
            pagination={pagination}
            onChange={tableOnChange}
            bordered
            size="middle"
            // scroll={{ x: 1800 }}
            scroll={{ x: "max-content" }}
            expandable={{
              expandedRowRender: renderTimeLogDetail,
              rowExpandable: (record: TimeLogListViewProps) =>
                (record.rawData?.timeLogResources?.length || 0) > 0 ||
                (record.rawData?.timeLogTools?.length || 0) > 0,
            }}
            // loading={loading}
          />
        </SkeletonTable>
      </div>

      {!isFromWorkUI && (
        <>
          <TimeSheetListCreation
            projectId={projectId}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            refreshData={refreshData}
            linkedTask={taskId}
            linkedIssue={issueId}
            disableLinkField={isFromSpecificLevel}
            taskOwnerId={taskOwnerId}
            taskOwnerName={taskOwnerName}
            onTaskCompletionUpdate={onTaskCompletionUpdate}
            issueOwnerId={issueOwnerId}
            issueOwnerName={issueOwnerName}
          />
        </>
      )}

      <RejectModal
        isModalVisible={rejectModalVisible}
        setIsModalVisible={setRejectModalVisible}
        onReject={handleRejectConfirm}
        title={`Reject Time Log: ${currentRejectItem?.timeLogTitle || ""}`}
        label="Reason for rejection"
        isLoading={isRejecting}
      />
    </div>
  );
};
